/**
 * Input sanitization utilities.
 *
 * React escapes JSX output automatically, so these helpers focus on:
 *  - Stripping null bytes and dangerous control characters before DB writes
 *  - Enforcing reasonable length limits
 *  - Normalising whitespace
 *  - Validating numeric ranges
 */

/** Remove null bytes and non-printable control chars (keeps \n \r \t for multiline) */
function stripControlChars(str: string, allowNewlines = false): string {
  if (allowNewlines) {
    // Keep \t (0x09), \n (0x0A), \r (0x0D) — strip everything else in C0/C1 range
    // eslint-disable-next-line no-control-regex
    return str.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, '');
  }
  // eslint-disable-next-line no-control-regex
  return str.replace(/[\x00-\x1F\x7F]/g, '');
}

/**
 * Sanitise a single-line text field (names, titles, codes, etc.).
 * Trims whitespace, strips control characters, truncates to maxLength.
 */
export function sanitizeText(value: unknown, maxLength = 255): string {
  if (!value || typeof value !== 'string') return '';
  return stripControlChars(value.trim()).slice(0, maxLength);
}

/**
 * Sanitise a multi-line text field (descriptions, messages).
 * Trims whitespace, strips control characters (preserving newlines), truncates.
 */
export function sanitizeMultilineText(value: unknown, maxLength = 2000): string {
  if (!value || typeof value !== 'string') return '';
  return stripControlChars(value.trim(), true).slice(0, maxLength);
}

/**
 * Sanitise an email address: trim and lowercase.
 * HTML type="email" + Supabase handle format validation.
 */
export function sanitizeEmail(value: unknown): string {
  if (!value || typeof value !== 'string') return '';
  return value.trim().toLowerCase().slice(0, 255);
}

export interface SanitizeNumberOptions {
  min?: number;
  max?: number;
  integer?: boolean;
  defaultValue?: number | null;
}

/**
 * Parse and clamp a numeric value to an optional [min, max] range.
 * Returns defaultValue if the input cannot be parsed as a finite number.
 */
export function sanitizeNumber(
  value: unknown,
  { min, max, integer = false, defaultValue = null }: SanitizeNumberOptions = {},
): number | null {
  const n = integer ? parseInt(String(value), 10) : parseFloat(String(value));
  if (!Number.isFinite(n)) return defaultValue ?? null;
  if (min !== undefined && n < min) return min;
  if (max !== undefined && n > max) return max;
  return n;
}
